import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import type { PaginationParams } from '@/types/params'
import type { Order } from '@/types/sorting'
import type { DeepCamelCase } from '@/types/utils'
import { formatApiDate } from '@/utils/format-date'
import { getOrdering } from '@/utils/get-ordering'
import { date, idAndNameSchema, requiredString } from '@/utils/zod'

import type { CurriculumAim } from '../constants/curriculum-aim'
import {
  curriculumAim,
  curriculumStatus
} from '../constants/curriculum-schemas'

type APIResponse = z.infer<typeof responseSchema>
export type CurriculumList = DeepCamelCase<APIResponse>
export type CurriculumListItem = CurriculumList[number]
export type CurriculumStatus = CurriculumListItem['status']

const responseSchema = z.array(
  z.object({
    id: requiredString(),
    name: requiredString(),
    education_level: idAndNameSchema,
    aim: curriculumAim,
    status: curriculumStatus,
    created: date(),
    can_be_deleted: z.boolean(),
    can_be_edited: z.boolean()
  })
)

const parseResponse = (response: APIResponse): CurriculumList =>
  response.map(item => ({
    id: item.id,
    name: item.name,
    educationLevel: item.education_level,
    aim: item.aim,
    status: item.status,
    created: item.created,
    canBeDeleted: item.can_be_deleted,
    canBeEdited: item.can_be_edited
  }))

type Filters = {
  curriculum?: string[]
  educationLevel?: string[]
  aim?: CurriculumAim[]
  schoolYearsFrom?: string
  schoolYearsTo?: string
  sort?: string
  order?: Order
}

type Params = Filters & PaginationParams

export const useCurriculums = (params: Params = {}) =>
  useQuery({
    queryKey: ['panelCurriculumList', params],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await httpService.panel.panelCurriculumsList({
        page: params.page ?? 1,
        page_size: params.pageSize ?? DEFAULT_PAGE_SIZE,
        id: params.curriculum,
        education_level_id: params.educationLevel,
        aim: params.aim,
        created_after: params.schoolYearsFrom
          ? formatApiDate(new Date(params.schoolYearsFrom))
          : undefined,
        created_before: params.schoolYearsTo
          ? formatApiDate(new Date(params.schoolYearsTo))
          : undefined,
        ordering: getOrdering(params),
        fetchKeys: {
          id: true,
          name: true,
          education_level: true,
          aim: true,
          status: true,
          created: true,
          can_be_deleted: true,
          can_be_edited: true
        }
      })

      const parsed = parseResponse(responseSchema.parse(response.results))
      return { list: parsed, count: response.count }
    }
  })
